import {
  DEFAULT_QR_POSTER_PAGE,
  QrPosterBlock,
  QrPosterTextFit,
  QrPosterSystemTemplateOption,
  QrPosterSystemTemplateVariant,
} from './qr-poster-template.model';

const A5_WIDTH = 148;
const A5_HEIGHT = 210;
const TOP_MARGIN = 5;
const TOP_BAR_HEIGHT = 20;
const FOOTER_HEIGHT = 15;
const QR_SIZE = 45;

const footerBlock = (): QrPosterBlock => ({
  id: 'footer-default',
  type: 'footer',
  x: 0,
  y: A5_HEIGHT - FOOTER_HEIGHT,
  width: A5_WIDTH,
  height: FOOTER_HEIGHT,
  locked: true,
  zIndex: 1000,
});

const applyColors = (style: Record<string, unknown>, c: Record<string, string>, keys: string[]): Record<string, unknown> => {
  const next = { ...style };
  keys.forEach((k) => {
    if (c[k] !== undefined) {
      next[k] = c[k];
    }
  });
  return next;
};

const SYSTEM_TEXT_FIT_SINGLE_LINE_IDS = new Set([
  'title',
  'subtitle',
  'header-title',
  'header-subtitle',
  'address-header',
  'company-header',
  'numbers-heading',
  'numbers-col-1-title',
  'numbers-col-1-subtitle',
  'numbers-col-1-value',
  'numbers-col-2-title',
  'numbers-col-2-value',
  'numbers-col-3-title',
  'numbers-col-3-value',
  'cta',
  'subtitle-main',
  'address-label',
  'scan-text-hier',
  'scan-text-scannen',
  'company-label',
  'company-name-badge-text',
]);

const SYSTEM_TEXT_FIT_SHRINK_IDS = new Set([
  'intro',
  'intro-text',
  'address-text',
  'address-line',
  'address-value',
  'scan-info-text',
  'title-main',
]);

const applySystemTemplateTextFit = (blocks: QrPosterBlock[]): QrPosterBlock[] => blocks.map((block) => {
  if (block.type !== 'text' && block.type !== 'variableText' && block.type !== 'iconMultilineText') {
    return block;
  }
  const textFit: QrPosterTextFit = SYSTEM_TEXT_FIT_SINGLE_LINE_IDS.has(block.id)
    ? 'singleLineShrink'
    : SYSTEM_TEXT_FIT_SHRINK_IDS.has(block.id)
      ? 'shrink'
      : 'wrap';
  return {
    ...block,
    style: {
      ...(block.style || {}),
      textFit,
    },
  };
});

const buildScanafyClassicBlocks = (): QrPosterBlock[] => [
  {
    id: 'header-bg',
    type: 'rectangle',
    x: 0,
    y: 5,
    width: 148,
    height: 20,
    zIndex: 1,
    style: { backgroundColor: '#d9d9d9' },
  },

  {
    id: 'title',
    type: 'text',
    x: 22,
    y: 9,
    width: 104,
    height: 6,
    zIndex: 20,
    content: { text: 'NOTFALL AUSHANG' },
    style: {
      fontSize: 16,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#000000',
    },
  },
  {
    id: 'subtitle',
    type: 'text',
    x: 18,
    y: 16,
    width: 112,
    height: 6,
    zIndex: 20,
    content: { text: 'Wichtige Kontaktinformationen auf einen SCAN' },
    style: {
      fontSize: 12,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#000000',
    },
  },
  {
    id: 'intro',
    type: 'text',
    x: 20.5,
    y: 27.67,
    width: 108,
    height: 17,
    zIndex: 20,
    content: { text: 'Scannen Sie den QR-Code, um schnell alle wichtigen Kontaktdaten, Dienstleistungen und Notfallnummern für dieses Gebäude zu erhalten.' },
    style: {
      fontSize: 12,
      textAlign: 'center',
      color: '#000000',
    },
  },

  {
    id: 'address-header',
    type: 'text',
    x: 24.5,
    y: 48.67,
    width: 100,
    height: 5,
    zIndex: 20,
    content: { text: 'Objektadresse' },
    style: {
      fontSize: 12,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#000000',
    },
  },
  {
    // iconMultilineText: icon dynamically positioned close to first text line,
    // eliminating fixed-icon issues for short vs long addresses
    id: 'address-line',
    type: 'iconMultilineText',
    x: 20,
    y: 53.67,
    width: 108,
    height: 12,
    zIndex: 30,
    content: { icon: 'environment', text: '{{property.address}}' },
    style: {
      fontSize: 12,
      textAlign: 'center',
      color: '#000000',
      iconColor: '#006fc9',
      gap: 3,
    },
  },

  {
    id: 'company-header',
    type: 'text',
    x: 20,
    y: 66.78,
    width: 108,
    height: 5,
    zIndex: 20,
    content: { text: 'Firmeninformationen' },
    style: {
      fontSize: 12,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#000000',
    },
  },
  {
    id: 'logo',
    type: 'companyLogo',
    x: 38,
    y: 75.78,
    width: 72,
    height: 20,
    zIndex: 20,
  },

  {
    id: 'company-name-row',
    type: 'iconText',
    x: 24,
    y: 99,
    width: 100,
    height: 7,
    zIndex: 40,
    content: { icon: 'home', text: '{{company.name}}' },
    style: {
      textAlign: 'center',
      fontSize: 12,
      color: '#000000',
      iconColor: '#006fc9',
      gap: 1.5,
    },
  },
  {
    id: 'company-phone-row',
    type: 'iconText',
    x: 24,
    y: 106,
    width: 100,
    height: 7,
    zIndex: 40,
    content: { icon: 'phone', text: '{{company.phone}}' },
    style: {
      textAlign: 'center',
      fontSize: 12,
      color: '#000000',
      iconColor: '#006fc9',
      gap: 1.5,
    },
  },
  {
    id: 'company-website-row',
    type: 'iconText',
    x: 24,
    y: 113,
    width: 100,
    height: 7,
    zIndex: 40,
    content: { icon: 'global', text: '{{company.website}}' },
    style: {
      textAlign: 'center',
      fontSize: 12,
      color: '#000000',
      iconColor: '#006fc9',
      gap: 1.5,
    },
  },

  {
    id: 'qr-frame',
    type: 'rectangle',
    x: 48.67,
    y: 122.83,
    width: 55.67,
    height: 50.99,
    zIndex: 5,
    style: {
      backgroundColor: '#ffffff',
      borderColor: '#2b2b2d',
      borderWidth: 0.8,
      color: '#111827',
    },
  },
  {
    id: 'qr',
    type: 'qrCode',
    x: 54,
    y: 125.82,
    width: 45,
    height: 45,
    zIndex: 30,
  },
  {
    id: 'cta',
    type: 'text',
    x: 54.5,
    y: 178.82,
    width: 40,
    height: 10,
    zIndex: 30,
    content: { text: 'Hier scannen' },
    style: {
      fontSize: 12,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#ffffff',
      backgroundColor: '#000000',
      borderRadius: 2,
    },
  },

  footerBlock(),
];

const buildInfoBoardBlocks = (): QrPosterBlock[] => [
  {
    id: 'header-bg',
    type: 'rectangle',
    x: 0,
    y: 0,
    width: 148,
    height: 24.85,
    zIndex: 1,
    style: { backgroundColor: '#18303a' },
  },
  {
    id: 'header-white-line',
    type: 'line',
    x: 0,
    y: 24.9,
    width: 148,
    height: 0.15,
    zIndex: 5,
    style: {
      borderColor: '#ffffff',
      borderWidth: 0.35,
    },
  },
  {
    id: 'header-bottom-strip',
    type: 'rectangle',
    x: 0,
    y: 25.25,
    width: 148,
    height: 3.55,
    zIndex: 2,
    style: { backgroundColor: '#435b67' },
  },

  {
    id: 'header-title',
    type: 'text',
    x: 6,
    y: 8.5,
    width: 100,
    height: 10,
    zIndex: 20,
    content: { text: 'Ihr digitaler Hausaushang' },
    style: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#ffffff',
    },
  },
  {
    id: 'header-subtitle',
    type: 'text',
    x: 5.6,
    y: 17.4,
    width: 78,
    height: 4,
    zIndex: 20,
    content: { text: 'Wichtige Kontaktinformationen auf einem Scan' },
    style: {
      fontSize: 7.6,
      color: '#dbe6e9',
    },
  },
  {
    id: 'header-logo',
    type: 'companyLogo',
    x: 106.5,
    y: 6.4,
    width: 36.5,
    height: 14,
    zIndex: 20,
  },

  {
    id: 'intro',
    type: 'text',
    x: 39,
    y: 36.2,
    width: 70,
    height: 12,
    zIndex: 20,
    content: {
      text: 'Scannen Sie den QR-Code, um schnell alle wichtigen Kontaktdaten, Dienstleistungen und Notfallnummern für dieses Gebäude zu erhalten.',
    },
    style: {
      fontSize: 8.2,
      textAlign: 'center',
      color: '#111111',
    },
  },

  {
    id: 'address-box',
    type: 'rectangle',
    x: 32,
    y: 52.2,
    width: 84,
    height: 10.8,
    zIndex: 4,
    style: {
      backgroundColor: '#f2f2f2',
      borderColor: '#c9c9c9',
      borderWidth: 0.4,
      borderRadius: 0,
    },
  },
  {
    id: 'address-label',
    type: 'text',
    x: 32,
    y: 53.8,
    width: 84,
    height: 2.6,
    zIndex: 20,
    rotation: 0,
    content: { text: 'Objektadresse' },
    style: {
      fontSize: 5.1,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#4b5563',
    },
  },
  {
    id: 'address-value',
    type: 'text',
    x: 32,
    y: 56.8,
    width: 84,
    height: 4.2,
    zIndex: 20,
    content: { text: '{{property.address}}' },
    style: {
      fontSize: 8.6,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#111111',
    },
  },

  {
    id: 'qr-frame',
    type: 'rectangle',
    x: 32,
    y: 68,
    width: 84,
    height: 86,
    zIndex: 4,
    style: {
      backgroundColor: '#ffffff',
      borderColor: '#c9c9c9',
      borderWidth: 0.4,
      borderRadius: 0,
    },
  },
  {
    id: 'qr',
    type: 'qrCode',
    x: 40,
    y: 72.5,
    width: 68,
    height: 68,
    zIndex: 20,
  },
  {
    id: 'cta',
    type: 'text',
    x: 56.5,
    y: 144.7,
    width: 35,
    height: 6.7,
    zIndex: 20,
    content: { text: 'Hier scannen' },
    style: {
      fontSize: 6.2,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#ffffff',
      backgroundColor: '#173740',
      borderRadius: 0,
    },
  },

  {
    id: 'numbers-heading',
    type: 'text',
    x: 0,
    y: 160.8,
    width: 148,
    height: 6,
    zIndex: 20,
    content: { text: 'Notfallnummern' },
    style: {
      fontSize: 10.5,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#173740',
    },
  },

  {
    id: 'numbers-table',
    type: 'rectangle',
    x: 14,
    y: 167.2,
    width: 120,
    height: 18.2,
    zIndex: 4,
    style: {
      backgroundColor: '#f5f5f5',
      borderColor: '#d0d0d0',
      borderWidth: 0.15,
      borderRadius: 0,
    },
  },
  {
    id: 'numbers-divider-1',
    type: 'line',
    x: 56.2,
    y: 167.2,
    width: 0.15,
    height: 18.2,
    zIndex: 10,
    style: {
      borderColor: '#d0d0d0',
      borderWidth: 0.15,
    },
  },
  {
    id: 'numbers-divider-2',
    type: 'line',
    x: 97.4,
    y: 167.2,
    width: 0.15,
    height: 18.2,
    zIndex: 10,
    style: {
      borderColor: '#d0d0d0',
      borderWidth: 0.15,
    },
  },

  {
    id: 'numbers-col-1-title',
    type: 'text',
    x: 14,
    y: 170,
    width: 42.2,
    height: 3,
    zIndex: 20,
    content: { text: 'Gasgeruch' },
    style: {
      fontSize: 5.8,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#111111',
    },
  },
  {
    id: 'numbers-col-1-subtitle',
    type: 'text',
    x: 14,
    y: 173,
    width: 42.2,
    height: 3,
    zIndex: 20,
    content: { text: 'Beispiel Netzservice' },
    style: {
      fontSize: 4.8,
      textAlign: 'center',
      color: '#6b7280',
    },
  },
  {
    id: 'numbers-col-1-value',
    type: 'text',
    x: 14,
    y: 178.4,
    width: 42.2,
    height: 5,
    zIndex: 20,
    content: { text: '0800 - 123 4567' },
    style: {
      fontSize: 9.8,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#111111',
    },
  },

  {
    id: 'numbers-col-2-title',
    type: 'text',
    x: 56.2,
    y: 170,
    width: 41.2,
    height: 3,
    zIndex: 20,
    content: { text: 'Polizei' },
    style: {
      fontSize: 5.8,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#111111',
    },
  },
  {
    id: 'numbers-col-2-value',
    type: 'text',
    x: 56.2,
    y: 176.5,
    width: 41.2,
    height: 7,
    zIndex: 20,
    content: { text: '110' },
    style: {
      fontSize: 16,
      fontWeight: 'normal',
      textAlign: 'center',
      color: '#111111',
    },
  },

  {
    id: 'numbers-col-3-title',
    type: 'text',
    x: 97.4,
    y: 170,
    width: 36.6,
    height: 3,
    zIndex: 20,
    content: { text: 'Feuerwehr / Rettung' },
    style: {
      fontSize: 5.8,
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#111111',
    },
  },
  {
    id: 'numbers-col-3-value',
    type: 'text',
    x: 97.4,
    y: 176.5,
    width: 36.6,
    height: 7,
    zIndex: 20,
    content: { text: '112' },
    style: {
      fontSize: 16,
      fontWeight: 'normal',
      textAlign: 'center',
      color: '#111111',
    },
  },
  footerBlock(),
];

const buildOfficialNoticeBlocks = (): QrPosterBlock[] => [
  {
    id: 'header-bg',
    type: 'rectangle',
    x: 0, y: 0, width: 148, height: 24,
    zIndex: 1,
    style: { backgroundColor: '#1e3a5f' },
  },
  {
    id: 'header-accent-line',
    type: 'line',
    x: 0, y: 24, width: 148, height: 0.2,
    zIndex: 5,
    style: { borderColor: '#3b82f6', borderWidth: 0.2 },
  },
  {
    id: 'title',
    type: 'text',
    x: 10, y: 7, width: 128, height: 8,
    zIndex: 20,
    content: { text: 'NOTFALL AUSHANG' },
    style: { fontSize: 16, fontWeight: 'bold', color: '#ffffff' },
  },
  {
    id: 'subtitle',
    type: 'text',
    x: 10, y: 17, width: 128, height: 5,
    zIndex: 20,
    content: { text: 'Wichtige Kontaktinformationen auf einen SCAN' },
    style: { fontSize: 8, color: '#94a3b8' },
  },
  {
    id: 'intro',
    type: 'text',
    x: 20, y: 32, width: 108, height: 12,
    zIndex: 20,
    content: { text: 'Scannen Sie den QR-Code, um schnell alle wichtigen Kontaktdaten, Dienstleistungen und Notfallnummern für dieses Gebäude zu erhalten.' },
    style: { fontSize: 9, textAlign: 'center', color: '#334155' },
  },
  {
    id: 'address-box',
    type: 'rectangle',
    x: 14, y: 48, width: 120, height: 18,
    zIndex: 2,
    style: { backgroundColor: '#f8fafc', borderColor: '#94a3b8', borderWidth: 0.4, borderRadius: 2 },
  },
  {
    id: 'address-header',
    type: 'text',
    x: 20, y: 50, width: 108, height: 4,
    zIndex: 20,
    content: { text: 'Objektadresse' },
    style: { fontSize: 7.5, fontWeight: 'bold', textAlign: 'center', color: '#64748b' },
  },
  {
    // iconMultilineText: icon dynamically positioned close to first text line
    id: 'address-line',
    type: 'iconMultilineText',
    x: 20, y: 54.5, width: 108, height: 10,
    zIndex: 20,
    content: { icon: 'environment', text: '{{property.address}}' },
    style: { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color: '#1e293b', iconColor: '#1e3a5f', gap: 2.5 },
  },
  {
    id: 'qr-box',
    type: 'rectangle',
    x: 14, y: 72, width: 120, height: 68,
    zIndex: 2,
    style: { backgroundColor: '#f8fafc', borderColor: '#94a3b8', borderWidth: 0.4, borderRadius: 2 },
  },
  {
    id: 'qr',
    type: 'qrCode',
    x: 44, y: 78, width: 56, height: 56,
    zIndex: 20,
  },
  {
    id: 'cta',
    type: 'text',
    x: 48, y: 146, width: 52, height: 8.5,
    zIndex: 20,
    content: { text: 'Hier scannen' },
    style: { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color: '#ffffff', backgroundColor: '#1e3a5f', borderRadius: 2 },
  },
  {
    id: 'company-box',
    type: 'rectangle',
    x: 14, y: 162, width: 120, height: 24,
    zIndex: 2,
    style: { backgroundColor: '#f8fafc', borderColor: '#94a3b8', borderWidth: 0.4, borderRadius: 2 },
  },
  {
    id: 'company-header',
    type: 'text',
    x: 20, y: 164, width: 108, height: 4,
    zIndex: 20,
    content: { text: 'Firmeninformationen' },
    style: { fontSize: 7.2, fontWeight: 'bold', textAlign: 'center', color: '#64748b' },
  },
  {
    id: 'company-name-row',
    type: 'iconText',
    x: 20, y: 169, width: 108, height: 5.5,
    zIndex: 20,
    content: { icon: 'home', text: '{{company.name}}' },
    style: { textAlign: 'center', fontSize: 9, color: '#1e293b', iconColor: '#1e3a5f', gap: 1.5 },
  },
  {
    id: 'company-phone-row',
    type: 'iconText',
    x: 20, y: 175, width: 108, height: 5.5,
    zIndex: 20,
    content: { icon: 'phone', text: '{{company.phone}}' },
    style: { textAlign: 'center', fontSize: 9, color: '#1e293b', iconColor: '#1e3a5f', gap: 1.5 },
  },
  {
    id: 'company-website-row',
    type: 'iconText',
    x: 20, y: 181, width: 108, height: 5,
    zIndex: 20,
    content: { icon: 'global', text: '{{company.website}}' },
    style: { textAlign: 'center', fontSize: 7.5, color: '#1e293b', iconColor: '#1e3a5f', gap: 1.5 },
  },
  {
    id: 'logo',
    type: 'companyLogo',
    x: 109, y: 4, width: 32, height: 18,
    zIndex: 30,
  },
  footerBlock(),
];

const buildScanafyModernBlocks = (): QrPosterBlock[] => [
  {
    id: 'page-border', type: 'rectangle',
    x: 3, y: 2, width: 142, height: 181, zIndex: 1,
    style: { backgroundColor: '#ffffff', borderColor: '#2A246A', borderWidth: 0.8, borderRadius: 4, opacity: 1 },
  },
  {
    id: 'header-bg', type: 'rectangle',
    x: 3, y: 2, width: 142, height: 31, zIndex: 2,
    style: { backgroundColor: '#2A246A', borderRadius: 4, opacity: 1 },
  },
  {
    id: 'title-main', type: 'text',
    x: 10, y: 5, width: 128, height: 14, zIndex: 30,
    content: { text: 'NOTFALL AUSHANG' },
    style: { fontSize: 26, fontWeight: 'bold', textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'subtitle-main', type: 'text',
    x: 14, y: 21, width: 120, height: 8, zIndex: 30,
    content: { text: 'Wichtige Kontaktinformationen auf einen Scan' },
    style: { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'header-divider', type: 'line',
    x: 58, y: 29, width: 32, height: 0.1, zIndex: 30,
    style: { borderColor: '#ffffff', borderWidth: 0.4 },
  },
  {
    id: 'intro-text', type: 'text',
    x: 22, y: 36, width: 104, height: 16, zIndex: 20,
    content: { text: 'Scannen Sie den QR-Code, um schnell alle wichtigen Kontaktdaten, Dienstleistungen und Notfallnummern für dieses Gebäude zu erhalten.' },
    style: { fontSize: 9, textAlign: 'center', color: '#111111' },
  },
  {
    id: 'address-bar-bg', type: 'rectangle',
    x: 12, y: 56, width: 124, height: 17, zIndex: 5,
    style: { backgroundColor: '#F5F3FA', borderRadius: 2, opacity: 1 },
  },
  {
    id: 'address-icon', type: 'icon',
    x: 18, y: 58.5, width: 11, height: 11, zIndex: 30,
    content: { icon: 'environment' },
    style: { color: '#2A246A' },
  },
  {
    id: 'address-label', type: 'text',
    x: 33, y: 60.5, width: 28, height: 7, zIndex: 30,
    content: { text: 'Objektadresse' },
    style: { fontSize: 8, fontWeight: 'bold', textAlign: 'left', color: '#2A246A' },
  },
  {
    id: 'address-separator', type: 'line',
    x: 64, y: 60.5, width: 0.1, height: 8, zIndex: 30,
    style: { borderColor: '#9B96B7', borderWidth: 0.3 },
  },
  {
    id: 'address-value', type: 'text',
    x: 70, y: 58.5, width: 60, height: 9, zIndex: 30,
    content: { text: '{{property.address}}' },
    style: { fontSize: 8, textAlign: 'center', color: '#111111' },
  },
  {
    id: 'scan-frame', type: 'rectangle',
    x: 12, y: 76, width: 124, height: 68, zIndex: 4,
    style: { backgroundColor: '#ffffff', borderColor: '#2A246A', borderWidth: 0.8, borderRadius: 3, opacity: 1 },
  },
  {
    id: 'scan-panel', type: 'rectangle',
    x: 12, y: 76, width: 54, height: 68, zIndex: 6,
    style: { backgroundColor: '#2A246A', borderRadius: 3, opacity: 1 },
  },
  {
    id: 'qr-inner-frame', type: 'rectangle',
    x: 72, y: 80, width: 58, height: 56, zIndex: 8,
    style: { backgroundColor: '#ffffff', borderColor: '#2A246A', borderWidth: 0.5, borderRadius: 2, opacity: 1 },
  },
  {
    id: 'scan-corner-top-left-h', type: 'line',
    x: 22, y: 84, width: 5, height: 0.1, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-top-left-v', type: 'line',
    x: 22, y: 84, width: 0.1, height: 5, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-top-right-h', type: 'line',
    x: 44, y: 84, width: 5, height: 0.1, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-top-right-v', type: 'line',
    x: 49, y: 84, width: 0.1, height: 5, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-bottom-left-h', type: 'line',
    x: 22, y: 96, width: 5, height: 0.1, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-bottom-left-v', type: 'line',
    x: 22, y: 91, width: 0.1, height: 5, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-bottom-right-h', type: 'line',
    x: 44, y: 96, width: 5, height: 0.1, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-corner-bottom-right-v', type: 'line',
    x: 49, y: 91, width: 0.1, height: 5, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.8 },
  },
  {
    id: 'scan-text-hier', type: 'text',
    x: 22, y: 103, width: 32, height: 10, zIndex: 35,
    content: { text: 'HIER' },
    style: { fontSize: 17, fontWeight: 'bold', textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'scan-text-scannen', type: 'text',
    x: 21, y: 114, width: 34, height: 7, zIndex: 35,
    content: { text: 'SCANNEN' },
    style: { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'scan-small-divider', type: 'line',
    x: 26, y: 125, width: 26, height: 0.1, zIndex: 35,
    style: { borderColor: '#ffffff', borderWidth: 0.4 },
  },
  {
    id: 'scan-info-text', type: 'text',
    x: 20, y: 131, width: 40, height: 8, zIndex: 35,
    content: { text: 'Alle wichtigen Informationen auf einen Scan' },
    style: { fontSize: 7, textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'qr-code-main', type: 'qrCode',
    x: 75, y: 82, width: 52, height: 52, zIndex: 20,
  },
  {
    id: 'company-frame', type: 'rectangle',
    x: 12, y: 148, width: 124, height: 34, zIndex: 4,
    style: { backgroundColor: '#ffffff', borderColor: '#2A246A', borderWidth: 0.7, borderRadius: 2.5, opacity: 1 },
  },
  {
    id: 'company-label-bg', type: 'rectangle',
    x: 48, y: 145, width: 52, height: 7, zIndex: 30,
    style: { backgroundColor: '#2A246A', borderRadius: 1.5, opacity: 1 },
  },
  {
    id: 'company-label', type: 'text',
    x: 50, y: 146.2, width: 48, height: 4.5, zIndex: 35,
    content: { text: 'FIRMENINFORMATIONEN' },
    style: { fontSize: 6.2, fontWeight: 'bold', textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'company-logo', type: 'companyLogo',
    x: 14, y: 153, width: 56, height: 18, zIndex: 20,
  },
  {
    id: 'company-name-badge', type: 'rectangle',
    x: 14, y: 171, width: 56, height: 6, zIndex: 8,
    style: { backgroundColor: '#2A246A', borderRadius: 1, opacity: 1 },
  },
  {
    id: 'company-name-badge-text', type: 'text',
    x: 15, y: 172, width: 54, height: 4.5, zIndex: 30,
    content: { text: '{{company.name}}' },
    style: { fontSize: 5.5, fontWeight: 'bold', textAlign: 'center', color: '#ffffff' },
  },
  {
    id: 'company-separator', type: 'line',
    x: 74, y: 152, width: 0.1, height: 28, zIndex: 20,
    style: { borderColor: '#9B96B7', borderWidth: 0.3 },
  },
  {
    id: 'company-row-name', type: 'iconText',
    x: 80, y: 152, width: 50, height: 8, zIndex: 35,
    content: { icon: 'home', text: '{{company.name}}' },
    style: { textAlign: 'left', fontSize: 7, color: '#111111', iconColor: '#2A246A', gap: 2 },
  },
  {
    id: 'company-row-divider-1', type: 'line',
    x: 82, y: 161, width: 46, height: 0.1, zIndex: 20,
    style: { borderColor: '#B7B3C9', borderWidth: 0.25 },
  },
  {
    id: 'company-row-phone', type: 'iconText',
    x: 80, y: 163, width: 50, height: 8, zIndex: 35,
    content: { icon: 'phone', text: '{{company.phone}}' },
    style: { textAlign: 'left', fontSize: 7, color: '#111111', iconColor: '#2A246A', gap: 2 },
  },
  {
    id: 'company-row-divider-2', type: 'line',
    x: 82, y: 172, width: 46, height: 0.1, zIndex: 20,
    style: { borderColor: '#B7B3C9', borderWidth: 0.25 },
  },
  {
    id: 'company-row-website', type: 'iconText',
    x: 80, y: 174, width: 50, height: 7, zIndex: 35,
    content: { icon: 'global', text: '{{company.website}}' },
    style: { textAlign: 'left', fontSize: 6.2, color: '#111111', iconColor: '#2A246A', gap: 2 },
  },
  footerBlock(),
];

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hVal = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hVal = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: hVal = ((b - r) / d + 2) / 6; break;
      case b: hVal = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(hVal * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h: number, s: number, l: number): string {
  const sl = s / 100;
  const ll = l / 100;
  const a = sl * Math.min(ll, 1 - ll);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = ll - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function derivePalette(primaryColor: string): Record<string, string> {
  const hsl = hexToHsl(primaryColor);
  const primaryL = hsl.l;
  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

  const primaryDeep = hslToHex(hsl.h, hsl.s, clamp(primaryL * 0.58, 16, 24));
  const primaryDark = hslToHex(hsl.h, hsl.s, clamp(primaryL * 0.75, 22, 32));
  const primarySoft = hslToHex(hsl.h, Math.max(6, hsl.s - 20), clamp(88 - primaryL * 0.08, 82, 90));
  const primaryPale = hslToHex(hsl.h, Math.max(3, hsl.s - 35), clamp(96 - primaryL * 0.04, 90, 96));
  const borderMuted = hslToHex(hsl.h, Math.max(3, hsl.s - 30), 82);

  return {
    primary: primaryColor,
    headerBg: primaryDeep,
    headerText: '#ffffff',
    accent: primaryDeep,
    accentLight: primarySoft,
    border: primaryDark,
    borderMuted: borderMuted,
    sectionBg: primaryPale,
    ctaBg: primaryDark,
    tableBg: primaryPale,
    tableBorder: borderMuted,
    iconColor: primaryDark,
    footerText: '#ffffff',
  };
}

const UNIFIED_VARIANTS: QrPosterSystemTemplateVariant[] = [
  { id: 'blue', name: 'Blue', colors: { primary: '#1e40af' } },
  { id: 'teal', name: 'Teal', colors: { primary: '#0f766e' } },
  { id: 'green', name: 'Green', colors: { primary: '#166534' } },
  { id: 'slate', name: 'Slate', colors: { primary: '#334155' } },
  { id: 'neutral', name: 'Neutral', colors: { primary: '#111827' } },
];

export const QR_POSTER_SYSTEM_TEMPLATES_FALLBACK: QrPosterSystemTemplateOption[] = [
  {
    id: 'scanafy-classic',
    name: 'Scanafy Classic',
    description: 'Matches the original Scanafy emergency poster layout with centered QR and company details.',
    qrPosterTemplate: {
      version: 1,
      selectedTemplateId: 'scanafy-classic',
      source: 'scanafy-classic',
      page: { ...DEFAULT_QR_POSTER_PAGE },
      branding: { showScanafyFooter: true },
      blocks: applySystemTemplateTextFit(buildScanafyClassicBlocks()),
    },
    variants: UNIFIED_VARIANTS,
  },
  {
    id: 'info-board',
    name: 'Info Board',
    description: 'A structured information notice with a dark header, centered QR section, and emergency numbers table.',
    qrPosterTemplate: {
      version: 1,
      selectedTemplateId: 'info-board',
      source: 'system-template',
      page: { ...DEFAULT_QR_POSTER_PAGE },
      branding: { showScanafyFooter: true },
      blocks: applySystemTemplateTextFit(buildInfoBoardBlocks()),
    },
    variants: UNIFIED_VARIANTS,
  },
  {
    id: 'official-notice',
    name: 'Official Notice',
    description: 'A formal notice board with a strong branded top bar, bordered sections, and structured contact layout.',
    qrPosterTemplate: {
      version: 1,
      selectedTemplateId: 'official-notice',
      source: 'system-template',
      page: { ...DEFAULT_QR_POSTER_PAGE },
      branding: { showScanafyFooter: true },
      blocks: applySystemTemplateTextFit(buildOfficialNoticeBlocks()),
    },
    variants: UNIFIED_VARIANTS,
  },
  {
    id: 'scanafy-modern',
    name: 'Scanafy Modern',
    description: 'A bold modern design with a deep purple palette, split-screen QR panel, and crisp structured layout.',
    qrPosterTemplate: {
      version: 1,
      selectedTemplateId: 'scanafy-modern',
      source: 'system-template',
      page: { ...DEFAULT_QR_POSTER_PAGE },
      branding: { showScanafyFooter: true },
      blocks: applySystemTemplateTextFit(buildScanafyModernBlocks()),
    },
    variants: UNIFIED_VARIANTS,
  },
];

export function getSystemTemplateVariant(templateId: string, variantId: string): QrPosterSystemTemplateVariant | undefined {
  const template = QR_POSTER_SYSTEM_TEMPLATES_FALLBACK.find((t) => t.id === templateId);
  return template?.variants?.find((v) => v.id === variantId);
}

export function applyVariantColorsToBlocks(
  blocks: QrPosterBlock[],
  variant: QrPosterSystemTemplateVariant | undefined
): QrPosterBlock[] {
  if (!variant || !variant.colors) {
    return blocks;
  }
  const colors = variant.colors.primary
    ? derivePalette(variant.colors.primary)
    : variant.colors;

  return blocks.map((block) => {
    const style = { ...(block.style || {}) } as Record<string, unknown>;
    if (colors.headerBg && (
      (block.id === 'header-bg' || block.id.endsWith('-bg')) && !block.id.startsWith('address-'))) {
      style['backgroundColor'] = colors.headerBg;
    }
    if (colors.headerText && (
      block.id === 'title' || block.id === 'subtitle' || block.id === 'header-title' || block.id === 'header-subtitle'
      || block.id === 'title-main' || block.id === 'subtitle-main')) {
      style['color'] = colors.headerText;
    }
    if (colors.accent && (
      block.id.includes('header-accent') || block.id.includes('cta') || block.id === 'scan-panel'
      || block.id === 'scan-notch'
      || block.id === 'company-name-badge' || block.id === 'company-label-bg' || block.id.endsWith('-badge')
      || block.id.endsWith('label-bg'))) {
      if (block.id === 'cta' || block.id.includes('cta')) {
        style['backgroundColor'] = colors.ctaBg || colors.accent;
      } else {
        style['backgroundColor'] = colors.accent;
      }
    }
    if (colors.accentLight && block.id.includes('header-subtitle')) {
      style['color'] = colors.accentLight;
    }
    if (colors.border && (
      block.id.includes('-box') || block.id.includes('-card') || block.id.includes('-section') || block.id.includes('-strip')
      || block.id.includes('-frame') || block.id.includes('-border')
      || block.id === 'address-card' || block.id === 'qr-card' || block.id === 'company-card'
      || block.id === 'qr-box' || block.id === 'company-box' || block.id === 'address-section' || block.id === 'qr-section'
      || block.id === 'company-strip')) {
      style['borderColor'] = colors.border;
    }
    if (colors.borderMuted && (
      block.id.includes('separator') || block.id.includes('divider') || block.id.includes('address-separator')
      || block.id.includes('company-separator') || block.id.includes('company-row-divider'))) {
      style['borderColor'] = colors.borderMuted;
    }
    if (colors.sectionBg && (
      block.id.includes('address-bar') || block.id.includes('address-bg') || block.id.startsWith('address-b')
      || block.id.includes('-box') || block.id.includes('-card') || block.id.includes('-section') || block.id.includes('-strip')
      || block.id === 'address-card' || block.id === 'qr-card' || block.id === 'company-card'
      || block.id === 'qr-box' || block.id === 'company-box' || block.id === 'address-section' || block.id === 'qr-section'
      || block.id === 'company-strip' || block.id === 'page-bg')) {
      style['backgroundColor'] = colors.sectionBg;
    }
    if (colors.iconColor && (
      block.type === 'icon' || block.type === 'iconText' || block.type === 'iconMultilineText')) {
      if (block.type === 'icon') {
        style['color'] = colors.iconColor;
      } else {
        style['iconColor'] = colors.iconColor;
      }
    }
    if (colors.iconColor && (
      block.id === 'address-label' || (block.id.includes('label') && block.id !== 'company-label'))) {
      style['color'] = colors.iconColor;
    }
    if (colors.ctaBg && block.id === 'cta') {
      style['backgroundColor'] = colors.ctaBg;
    }
    if (colors.tableBg && (
      block.id === 'numbers-table' || block.id.includes('table'))) {
      style['backgroundColor'] = colors.tableBg;
    }
    if (colors.tableBorder && (
      block.id === 'numbers-table' || block.id.includes('table') || block.id.includes('numbers-divider'))) {
      style['borderColor'] = colors.tableBorder;
    }
    if (colors.footerText && block.id.includes('footer')) {
      style['color'] = colors.footerText;
    }
    if (block.type === 'footer' && (colors.accent || colors.ctaBg)) {
      style['backgroundColor'] = colors.ctaBg || colors.accent || '';
    }
    if (colors.ctaBg && block.id.includes('numbers-heading')) {
      style['color'] = colors.ctaBg;
    }
    return { ...block, style };
  });
}
